/**
 * El alta guiada: "Producto nuevo" pregunta primero QUÉ se va a cargar.
 *
 * Cuatro pasos: ¿qué es? → los datos de esa cosa → precio y stock → revisión.
 * Las opciones "Usado" y "Otro" no son de este wizard: delegan en el asistente
 * de usados (ficha por unidad) y en el formulario completo (motor de precio),
 * que ya existen y hacen mejor ese trabajo.
 *
 * DOS COSAS QUE ESTE ARCHIVO NO HACE, A PROPOSITO
 * -----------------------------------------------
 *  1. NO tiene un bloque de campos por categoria. Lo que se pregunta de un
 *     perfume y lo que se pregunta de un vaper esta declarado en
 *     `CAMPOS_POR_TIPO`, y acá hay UN renderizador. Antes eran cinco bloques
 *     de JSX escritos a mano que se fueron pareciendo cada vez menos entre si.
 *  2. NO cuenta pasos que nadie va a caminar. Entrando desde una seccion el
 *     tipo ya esta elegido, asi que el camino son tres pasos y la barra dice
 *     "Paso 1 de 3", no "Paso 2 de 4" de un paso que nunca se vio. Lo que se
 *     esta cargando queda escrito arriba, con «Cambiar» al lado.
 */
import { memoriaQueSigue } from "@/components/iphones/memorias";
import { useEffect, useId, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, FileSliders, Smartphone } from "lucide-react";

import { CampoCantidad } from "@/components/catalogo/Campos";
import { InputImporte } from "@/components/money/input-importe";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo, GrupoCampo } from "@/components/ui/campo";
import {
  CamposDeOtraMarca,
  SelectorDeColor,
  SelectorDeMemoria,
  SelectorDeModelo,
} from "@/components/iphones/FichaDelEquipo";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Pasos } from "@/components/ui/pasos";

import { EquipoApple } from "./EquipoApple";
import { COLORES_DE_FAMILIA, faltaDelEquipoApple, preguntaColor } from "./equiposApple";

/** Las RAM en que vienen los celulares de otra marca. */
const RAM_CELULAR = [2, 3, 4, 6, 8, 12, 16];
import { Segmented } from "@/components/ui/segmented";
import { SelectNativo } from "@/components/ui/select-nativo";
import { type Producto, type Rubro } from "@/lib/tipos";
import { cn } from "@/lib/utils";

import {
  CAMPOS_POR_TIPO,
  FORM_ALTA_INICIAL,
  OPCIONES_ALTA,
  RUBROS_ACCESORIO,
  ayudaDelPrecio,
  erroresPorCampo,
  monedaInicial,
  nombreGenerado,
  parecidosAlAlta,
  resumenAlta,
  validarPrecio,
  validarUnidades,
  type CampoAlta,
  type ClaveCampo,
  type FormAlta,
  type TipoAlta,
  rubroSeDeduce,
} from "./logica";

type Paso = "tipo" | "datos" | "precio" | "revision";

/** Los pasos que se caminan de verdad. Con el tipo ya elegido, son tres. */
const CAMINO_COMPLETO: ReadonlyArray<Paso> = ["tipo", "datos", "precio", "revision"];
const CAMINO_CON_TIPO: ReadonlyArray<Paso> = ["datos", "precio", "revision"];

/** Precio y costo arrancan en la misma moneda; después cada uno va por su lado. */
function formInicial(tipo: TipoAlta, rubroId: number | null = null, nombreLibre = ""): FormAlta {
  const moneda = monedaInicial(tipo);
  return { ...FORM_ALTA_INICIAL, moneda, monedaCosto: moneda, rubroId, nombreLibre };
}

export interface DialogAltaProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  rubros: Rubro[];
  /** Para elegir quién tiene el producto cuando es «a pedido». */
  proveedores?: ReadonlyArray<{ id: number; nombre: string }>;
  guardando?: boolean;
  /** Para que el selector de moneda del costo convierta en vez de solo etiquetar. */
  cotizacion?: number;
  /**
   * El tipo que sugiere la sección activa: entra directo a sus preguntas, sin
   * pasar por el selector ("estás en Perfumes, cargás un perfume"). Con null
   * arranca en el selector, y «Cambiar» siempre lo tiene a mano.
   */
  tipoInicial?: TipoAlta | null;
  /**
   * Lo que se puede cargar desde la sección activa. Parado en «Otras marcas»
   * no se ofrece un iPhone sellado ni un perfume: el selector, y el «Cambiar»
   * que lleva a él, muestran solo lo que va en esa solapa. Sin esto, todo.
   */
  tipos?: ReadonlyArray<TipoAlta>;
  /** Si la sección admite usados (el asistente de usados como opción). */
  conUsado?: boolean;
  /**
   * Las familias entre las que elige un «Equipo», por nombre. En Apple son
   * Mac, iPad, Watch…; en Otros equipos, tablets, relojes y consolas. Si queda
   * una sola, viene elegida.
   */
  familiasDelEquipo?: ReadonlyArray<string>;
  /**
   * Lo ya cargado, para avisar en la revisión si lo que se crea ya existe con
   * otro nombre. Sin esto no se avisa.
   */
  existentes?: ReadonlyArray<Producto>;
  /**
   * Lo que se venía buscando cuando no apareció («¿No está? Crear producto
   * nuevo» del Ingreso). Precarga el nombre de lo que se escribe a mano: antes
   * llegaba vacío y había que tipear otra vez lo que se acababa de buscar.
   */
  nombreInicial?: string;
  /** El wizard resolvió todo: crear el producto (y las unidades, si hay). */
  onCrear: (tipo: TipoAlta, form: FormAlta) => void;
  /** Eligió "Usado": abre el asistente de usados. */
  onUsado?: () => void;
  /**
   * Eligió "Otro": abre el formulario completo. Sin esto la opción no se
   * ofrece (desde Inventario no hay formulario completo a mano).
   */
  onCompleto?: () => void;
}

const CLASE_OPCION = "h-auto w-full justify-start whitespace-normal px-4 py-3 text-left";

export function DialogAlta({
  abierto,
  onAbiertoChange,
  rubros,
  proveedores = [],
  guardando = false,
  cotizacion,
  tipoInicial = null,
  tipos,
  conUsado = true,
  familiasDelEquipo,
  existentes = [],
  nombreInicial = "",
  onCrear,
  onUsado,
  onCompleto,
}: DialogAltaProps): JSX.Element {
  const [paso, setPaso] = useState<Paso>("tipo");
  const [tipo, setTipo] = useState<TipoAlta>("iphone");
  const [form, setForm] = useState<FormAlta>(FORM_ALTA_INICIAL);
  const [intento, setIntento] = useState(false);

  const opciones = tipos ? OPCIONES_ALTA.filter((o) => tipos.includes(o.tipo)) : OPCIONES_ALTA;
  // «Cambiar» solo si hay a qué cambiar: con un tipo solo y sin usados, el
  // selector repetiría lo mismo más el formulario completo.
  const hayOtrasOpciones = opciones.length > 1 || conUsado;

  const familias = rubros.filter((r) => r.activo && r.nombre !== "A pedido");
  const familiasEquipo = familiasDelEquipo
    ? familias.filter((r) => familiasDelEquipo.includes(r.nombre))
    : familias;
  const unicaFamilia = familiasEquipo.length === 1 ? familiasEquipo[0]!.id : null;

  function formDe(t: TipoAlta): FormAlta {
    return formInicial(t, t === "equipo" ? unicaFamilia : null, nombreInicial.trim());
  }

  useEffect(() => {
    if (!abierto) return;
    const inicial = tipoInicial ?? "iphone";
    setPaso(tipoInicial ? "datos" : "tipo");
    setTipo(inicial);
    setForm(formDe(inicial));
    setIntento(false);
    // formDe depende de las familias, que llegan con los rubros: sembrar de
    // nuevo cuando llegan borraría lo tipeado, y no hace falta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, tipoInicial]);

  const camino = tipoInicial ? CAMINO_CON_TIPO : CAMINO_COMPLETO;

  const erroresDatos = useMemo(() => erroresPorCampo(tipo, form), [tipo, form]);
  const parecidos = useMemo(
    () => (paso === "revision" ? parecidosAlAlta(tipo, form, existentes) : []),
    [paso, tipo, form, existentes],
  );
  const erroresPrecio = useMemo(
    () => [...validarPrecio(form, cotizacion), ...validarUnidades(tipo, form)],
    [form, cotizacion, tipo],
  );
  const hayErrores =
    paso === "datos"
      ? Object.keys(erroresDatos).length > 0
      : paso === "precio"
        ? erroresPrecio.length > 0
        : false;

  function cambiar(cambios: Partial<FormAlta>): void {
    setForm((f) => ({ ...f, ...cambios }));
  }

  function elegirTipo(t: TipoAlta): void {
    // Volver al selector y elegir lo MISMO no puede borrar lo ya escrito: el
    // que entró a mirar las opciones y salió por donde vino no perdió nada.
    if (t !== tipo) {
      setTipo(t);
      setForm(formDe(t));
    }
    setIntento(false);
    setPaso("datos");
  }

  function avanzar(): void {
    if (hayErrores) {
      setIntento(true);
      return;
    }
    setIntento(false);
    if (paso === "datos") setPaso("precio");
    else if (paso === "precio") setPaso("revision");
  }

  function volver(): void {
    setIntento(false);
    const i = camino.indexOf(paso);
    const anterior = camino[i - 1];
    if (anterior) setPaso(anterior);
  }

  const opcion = OPCIONES_ALTA.find((o) => o.tipo === tipo);
  // Un «Equipo» cargado desde una seccion dice cuales son sus familias: en
  // Apple no es «tablets, consolas, camaras», es «Apple, Mac, iPad…».
  const detalle =
    tipo === "equipo" && familiasDelEquipo
      ? familiasEquipo.map((r) => r.nombre).join(", ")
      : opcion?.detalle;
  const rubrosAccesorio = rubros.filter((r) => RUBROS_ACCESORIO.includes(r.nombre));
  // Lo «a pedido» va en la familia de la cosa. El rubro «A pedido» que queda
  // en las bases viejas no se ofrece: no esta publicado (0099). Un «Equipo»
  // elige solo entre las familias de su seccion.
  const familiasDelTipo = tipo === "equipo" ? familiasEquipo : familias;
  // Las marcas que ya están cargadas, para sugerirlas en la del accesorio.
  const marcas = useMemo(
    () =>
      [...new Set(existentes.map((p) => p.marca?.trim() ?? "").filter((m) => m !== ""))].sort(
        (a, b) => a.localeCompare(b, "es"),
      ),
    [existentes],
  );

  // El titulo del dialogo no cambia entre pasos: lo que cambia es el titulo
  // del paso, en la barra. El del paso de datos es el tipo elegido.
  const TITULOS: Record<Paso, string> = {
    tipo: "¿Qué vas a cargar?",
    datos: opcion?.titulo ?? "Los datos",
    precio: "Precio y stock",
    revision: "Revisá antes de guardar",
  };
  // Fuera del camino (el selector al que se llega con «Cambiar») no hay barra:
  // es un desvío, no un paso, y numerarlo mentiría sobre cuánto falta.
  const indice = camino.indexOf(paso);
  const nombre = nombreGenerado(tipo, form);

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Producto nuevo</DialogTitle>
          {/* La descripcion del dialogo (aria-describedby) es el texto del paso
              mas "Paso N de M · titulo", que `Pasos` escribe arriba de la barra. */}
          <DialogDescription asChild>
            <div className="space-y-2">
              <p>
                {paso === "tipo"
                  ? "Cada cosa se carga distinto. Elegí y el resto son dos o tres preguntas."
                  : "El nombre del catálogo se arma solo con lo que contestes."}
              </p>
              {indice >= 0 ? <Pasos pasos={camino.map((p) => TITULOS[p])} actual={indice} /> : null}
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {paso === "tipo" ? (
            <div role="group" aria-label={TITULOS.tipo} className="flex flex-col gap-2">
              {/* Tarjetas-opcion: elegir avanza, por eso no hay marcada. */}
              {opciones.map((o) => (
                <Button
                  key={o.tipo}
                  type="button"
                  variant="outline"
                  onClick={() => elegirTipo(o.tipo)}
                  className={CLASE_OPCION}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{o.titulo}</span>
                    <span className="block text-xs font-normal text-muted-foreground">
                      {o.detalle}
                    </span>
                  </span>
                </Button>
              ))}
              {conUsado && onUsado ? (
                <Button type="button" variant="outline" onClick={onUsado} className={CLASE_OPCION}>
                  <Smartphone className="text-muted-foreground" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">Usado</span>
                    <span className="block text-xs font-normal text-muted-foreground">
                      Una unidad con su ficha: batería, IMEI, costo de toma.
                    </span>
                  </span>
                </Button>
              ) : null}
              {onCompleto ? (
                <Button
                  type="button"
                  variant="dashed"
                  onClick={onCompleto}
                  className={CLASE_OPCION}
                >
                  <FileSliders aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">Otro / formulario completo</span>
                    <span className="block text-xs font-normal">
                      Markup, redondeo, códigos: todo el motor de precio.
                    </span>
                  </span>
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4">
              {/*
                  QUE SE ESTA CARGANDO, SIEMPRE A LA VISTA.

                  Entrando desde una seccion el tipo se eligio afuera, en la
                  solapa, y adentro del dialogo no habia rastro de eso: el
                  titulo decia "Producto nuevo" y los campos, cualesquiera que
                  fueran, no se explicaban solos. Esta tarjeta dice que cosa se
                  esta cargando, como va quedando el nombre, y como cambiarlo.
              */}
              <Card
                variant="muted"
                padding="compacto"
                className="flex flex-wrap items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">{opcion?.titulo}</p>
                  <p className="text-xs text-muted-foreground">{nombre || detalle}</p>
                </div>
                {paso === "datos" && hayOtrasOpciones ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setPaso("tipo")}>
                    Cambiar
                  </Button>
                ) : null}
              </Card>

              {paso === "datos" ? (
                <CamposDelTipo
                  tipo={tipo}
                  form={form}
                  cambiar={cambiar}
                  errores={intento ? erroresDatos : {}}
                  rubrosAccesorio={rubrosAccesorio}
                  familias={familiasDelTipo}
                  proveedores={proveedores}
                  marcas={marcas}
                />
              ) : null}

              {paso === "precio" ? (
                <>
                  <InputImporte
                    label="Precio de venta"
                    valor={form.precio}
                    onValorChange={(v) => cambiar({ precio: v })}
                    moneda={form.moneda}
                    onMonedaChange={(m) => cambiar({ moneda: m })}
                    cotizacion={cotizacion}
                    requerido
                    autoFocus
                    ayuda={ayudaDelPrecio(form, cotizacion)}
                  />
                  <InputImporte
                    label="Costo"
                    valor={form.costo}
                    onValorChange={(v) => cambiar({ costo: v })}
                    moneda={form.monedaCosto}
                    onMonedaChange={(m) => cambiar({ monedaCosto: m })}
                    cotizacion={cotizacion}
                    ayuda="Sirve para ver el margen. Se puede completar después."
                  />
                  {tipo === "apedido" ? (
                    <Card
                      variant="muted"
                      padding="compacto"
                      className="text-xs text-muted-foreground"
                    >
                      No se pregunta stock: lo tiene el proveedor. Venderlo lo encarga solo.
                    </Card>
                  ) : (
                    <CampoCantidad
                      label="¿Cuántas unidades tenés ahora?"
                      ayuda="0 o vacío: se carga la ficha sin stock."
                      valor={form.unidades}
                      onValorChange={(v) => cambiar({ unidades: v })}
                      placeholder="0"
                    />
                  )}
                </>
              ) : null}

              {paso === "revision" ? (
                <Card variant="muted" padding="compacto" className="text-sm">
                  <ul className="space-y-1">
                    {resumenAlta(tipo, form).map((l) => (
                      <li key={l}>{l}</li>
                    ))}
                  </ul>
                </Card>
              ) : null}

              {paso === "revision" && parecidos.length > 0 ? (
                <Aviso tono="warning" compacto titulo="¿Es alguno de estos?">
                  <ul className="space-y-1">
                    {parecidos.map((p) => (
                      <li key={p.id}>
                        {p.nombre}{" "}
                        <span className="text-muted-foreground">· {p.codigo_interno}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1">
                    Si es el mismo producto, no lo cargues de nuevo: cancelá y sumale stock con
                    Ingreso, en Inventario.
                  </p>
                </Aviso>
              ) : null}
            </div>
          )}

          {/* Error de formulario: lo que no es de un campo solo (el precio
              contra el costo). Lo que sí es de un campo se lee abajo del campo. */}
          {intento && paso === "precio" && erroresPrecio.length > 0 ? (
            <Aviso tono="destructive" compacto>
              <ul className="space-y-1">
                {erroresPrecio.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </Aviso>
          ) : null}
        </DialogBody>

        {/* En el paso "tipo" elegir una opcion avanza: el footer solo tiene Cancelar. */}
        <DialogFooter variant="wizard">
          <Button type="button" variant="ghost" onClick={() => onAbiertoChange(false)}>
            Cancelar
          </Button>

          {paso !== "tipo" ? (
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              {/* Sin "Atrás" en el primer paso del camino: no hay a dónde ir, y
                  el boton que llevaba al selector ahora dice «Cambiar». */}
              {indice > 0 ? (
                <Button type="button" variant="outline" onClick={volver} disabled={guardando}>
                  <ChevronLeft aria-hidden="true" />
                  Atrás
                </Button>
              ) : null}
              {paso !== "revision" ? (
                <Button type="button" onClick={avanzar}>
                  Siguiente
                  <ChevronRight aria-hidden="true" />
                </Button>
              ) : (
                <Button type="button" onClick={() => onCrear(tipo, form)} cargando={guardando}>
                  Cargar el producto
                </Button>
              )}
            </div>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Los campos, dibujados desde la declaración
// ─────────────────────────────────────────────────────────────────────────────

/**
 * UN SOLO renderizador para las cinco categorías.
 *
 * La grilla es la de siempre (`grid-cols-1` / `sm:grid-cols-2`) y cada campo
 * declara si ocupa media o entera. Lo obligatorio lleva asterisco y su error
 * debajo, sea un input, una cantidad o un grupo de chips: esa uniformidad es
 * todo el punto de esto.
 */
function CamposDelTipo({
  tipo,
  form,
  cambiar,
  errores,
  rubrosAccesorio,
  familias,
  proveedores,
  marcas,
}: {
  tipo: TipoAlta;
  form: FormAlta;
  cambiar: (cambios: Partial<FormAlta>) => void;
  errores: Partial<Record<ClaveCampo, string>>;
  rubrosAccesorio: ReadonlyArray<Rubro>;
  familias: ReadonlyArray<Rubro>;
  proveedores: ReadonlyArray<{ id: number; nombre: string }>;
  marcas: ReadonlyArray<string>;
}): JSX.Element {
  // El rubro de un equipo Apple no se pregunta: sale de lo elegido. El color,
  // solo en Mac, iPad y Watch, y cuando ya se sabe cuál es.
  const campos = CAMPOS_POR_TIPO[tipo].filter(
    (c) =>
      !(c.control === "familia" && rubroSeDeduce(form)) &&
      !(
        c.control === "colorEquipo" &&
        (!preguntaColor(form.equipo) || faltaDelEquipoApple(form.equipo) !== null)
      ),
  );
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {campos.map((campo, i) => (
        <div
          key={campo.clave}
          // Un campo puede ser dos preguntas (el modelo y su variante): con aire
          // entre ellas, como entre los campos de la grilla.
          className={cn("space-y-4", !campo.medio && "sm:col-span-2")}
        >
          <CampoDeAlta
            campo={campo}
            form={form}
            cambiar={cambiar}
            error={errores[campo.clave]}
            // El foco entra en la primera pregunta, siempre. Antes lo llevaba
            // un campo distinto en cada categoria, y en una ni siquiera estaba.
            autoFocus={i === 0}
            rubrosAccesorio={rubrosAccesorio}
            familias={familias}
            proveedores={proveedores}
            marcas={marcas}
          />
        </div>
      ))}
    </div>
  );
}

function CampoDeAlta({
  campo,
  form,
  cambiar,
  error,
  autoFocus,
  rubrosAccesorio,
  familias,
  proveedores,
  marcas,
}: {
  campo: CampoAlta;
  form: FormAlta;
  cambiar: (cambios: Partial<FormAlta>) => void;
  error?: string;
  autoFocus: boolean;
  rubrosAccesorio: ReadonlyArray<Rubro>;
  familias: ReadonlyArray<Rubro>;
  proveedores: ReadonlyArray<{ id: number; nombre: string }>;
  marcas: ReadonlyArray<string>;
}): JSX.Element {
  const { clave, label, ayuda, placeholder, requerido = false } = campo;
  const idMarcas = useId();

  if (campo.control === "colorEquipo") {
    const familia = form.equipo.familia;
    return (
      <ChipsConOtra
        // Otra familia, otros colores: la «Otra» abierta no se arrastra.
        key={familia}
        campo={{ ...campo, opciones: familia ? (COLORES_DE_FAMILIA[familia] ?? []) : [] }}
        valor={form.color}
        onValor={(v) => cambiar({ color: v === null ? "" : String(v) })}
        error={error}
      />
    );
  }

  if (campo.control === "marcaLibre") {
    // Escrita como una que ya existe («jbl»), se guarda como esa («JBL»): si
    // no, el filtro por marca las separa en dos.
    const conocida = (texto: string): string =>
      marcas.find((m) => m.toLowerCase() === texto.trim().toLowerCase()) ?? texto;
    // La lista de sugerencias va afuera de `Campo`, que le pone el id y la
    // descripcion a su unico hijo.
    return (
      <>
        <Campo label={label} ayuda={ayuda} error={error} requerido={requerido}>
          <Input
            autoComplete="off"
            list={idMarcas}
            placeholder={placeholder}
            value={form.marca}
            onChange={(e) => cambiar({ marca: e.target.value })}
            onBlur={(e) => cambiar({ marca: conocida(e.target.value) })}
          />
        </Campo>
        <datalist id={idMarcas}>
          {marcas.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </datalist>
      </>
    );
  }

  if (campo.control === "texto") {
    return (
      <Campo label={label} ayuda={ayuda} error={error} requerido={requerido}>
        <Input
          autoFocus={autoFocus}
          autoComplete="off"
          placeholder={placeholder}
          value={String(form[clave] ?? "")}
          onChange={(e) => cambiar({ [clave]: e.target.value } as Partial<FormAlta>)}
        />
      </Campo>
    );
  }

  if (campo.control === "cantidad") {
    return (
      <CampoCantidad
        label={label}
        ayuda={ayuda}
        error={error}
        requerido={requerido}
        autoFocus={autoFocus}
        placeholder={placeholder}
        valor={form[clave] as number | null}
        onValorChange={(v) => cambiar({ [clave]: v } as Partial<FormAlta>)}
      />
    );
  }

  if (campo.control === "memoria") {
    return (
      <SelectorDeMemoria
        modelo={form.modelo}
        memoriaGb={form.memoriaGb}
        onChange={(memoriaGb) => cambiar({ memoriaGb })}
        error={error}
      />
    );
  }

  /*
   * El modelo, la memoria y el color son los MISMOS controles que usa el
   * asistente de usados (`iphones/FichaDelEquipo`). Antes estaban escritos acá
   * y el de usados pedía el modelo con un campo de texto: un equipo se
   * describía distinto según viniera nuevo o usado, y el nombre salía con dos
   * formatos.
   */
  if (campo.control === "modelo") {
    return (
      <SelectorDeModelo
        modelo={form.modelo}
        // Cambiar de modelo borra el color: el "Desert Titanium" del 16 Pro no
        // existe en el 17, y un color que el modelo no tiene es un dato falso.
        onChange={(modelo) =>
          cambiar({ modelo, color: "", memoriaGb: memoriaQueSigue(modelo, form.memoriaGb) })
        }
        error={error}
        autoFocus={autoFocus}
      />
    );
  }

  if (campo.control === "otraMarca") {
    return (
      <CamposDeOtraMarca
        modelo={form.modelo}
        onChange={(modelo) => cambiar({ modelo })}
        error={error}
        autoFocus={autoFocus}
        conIphone={false}
      />
    );
  }

  if (campo.control === "color") {
    return (
      <SelectorDeColor
        modelo={form.modelo}
        color={form.color}
        onChange={(color) => cambiar({ color })}
        // Un sellado se pide en el color que el cliente quiera: sin elegir
        // ninguno, la ficha es la fila de lista de precios del modelo.
        admiteTodos
        ayuda={ayuda}
      />
    );
  }

  if (campo.control === "rubro") {
    // Sin elegir nada, el accesorio cae en «Accesorios»: el chip lo muestra
    // marcado para que se vea a dónde va a ir, no para que haya que tocarlo.
    const porDefecto = rubrosAccesorio.find((r) => r.nombre === "Accesorios")?.id ?? null;
    const activo = form.rubroId ?? porDefecto;
    return (
      <GrupoCampo label={label} ayuda={ayuda} error={error} requerido={requerido}>
        <Segmented
          apariencia="chips"
          tamano="md"
          etiqueta={label}
          opciones={rubrosAccesorio.map((r) => ({ valor: String(r.id), etiqueta: r.nombre }))}
          valor={activo === null ? "" : String(activo)}
          onChange={(v) => cambiar({ rubroId: Number(v) })}
        />
      </GrupoCampo>
    );
  }

  if (campo.control === "chips") {
    return (
      <ChipsConOtra
        campo={campo}
        valor={form[clave] as string | number | null}
        onValor={(v) => cambiar({ [clave]: v } as Partial<FormAlta>)}
        error={error}
      />
    );
  }

  if (campo.control === "ram") {
    return (
      <GrupoCampo label={label} ayuda={ayuda} error={error}>
        <Segmented
          apariencia="chips"
          tamano="md"
          etiqueta={label}
          opciones={RAM_CELULAR.map((gb) => ({ valor: String(gb), etiqueta: `${gb} GB` }))}
          valor={form.ramGb === null ? "" : String(form.ramGb)}
          // Tocar la elegida la suelta: la RAM es opcional.
          onChange={(v) => cambiar({ ramGb: form.ramGb === Number(v) ? null : Number(v) })}
        />
      </GrupoCampo>
    );
  }

  if (campo.control === "equipoApple") {
    return (
      <EquipoApple
        equipo={form.equipo}
        // Elegir una Mac pone su rubro; volver a «Otro equipo» lo suelta para
        // que se elija a mano.
        onEquipo={(equipo) =>
          cambiar({
            equipo,
            ...(equipo.familia === "OTRO" ? {} : { rubroId: null }),
            // Los colores son de cada familia: el «Midnight» de una Mac no se
            // arrastra a un iPad.
            ...(equipo.familia !== form.equipo.familia ? { color: "" } : {}),
          })
        }
        nombreLibre={form.nombreLibre}
        onNombreLibre={(nombreLibre) => cambiar({ nombreLibre })}
        error={error}
        placeholder={placeholder}
      />
    );
  }

  if (campo.control === "familia") {
    // Pocas familias —las de una seccion— se eligen con botones, como el rubro
    // de un accesorio. Todas juntas son demasiadas: van en la lista.
    if (familias.length <= 6) {
      return (
        <GrupoCampo label={label} ayuda={ayuda} error={error} requerido={requerido}>
          <Segmented
            apariencia="chips"
            tamano="md"
            etiqueta={label}
            opciones={familias.map((r) => ({ valor: String(r.id), etiqueta: r.nombre }))}
            valor={form.rubroId === null ? "" : String(form.rubroId)}
            onChange={(v) => cambiar({ rubroId: Number(v) })}
          />
        </GrupoCampo>
      );
    }
    return (
      <Campo label={label} ayuda={ayuda} error={error} requerido={requerido}>
        <SelectNativo
          autoFocus={autoFocus}
          value={form.rubroId === null ? "" : String(form.rubroId)}
          onChange={(e) => cambiar({ rubroId: e.target.value ? Number(e.target.value) : null })}
        >
          <option value="">Elegí el rubro</option>
          {familias.map((r) => (
            <option key={r.id} value={String(r.id)}>
              {r.nombre}
            </option>
          ))}
        </SelectNativo>
      </Campo>
    );
  }

  return (
    <Campo label={label} ayuda={ayuda} error={error} requerido={requerido}>
      <SelectNativo
        autoFocus={autoFocus}
        value={form.proveedorId === null ? "" : String(form.proveedorId)}
        onChange={(e) => cambiar({ proveedorId: e.target.value ? Number(e.target.value) : null })}
      >
        <option value="">Elegí el proveedor</option>
        {proveedores.map((pr) => (
          <option key={pr.id} value={String(pr.id)}>
            {pr.nombre}
          </option>
        ))}
      </SelectNativo>
    </Campo>
  );
}

/**
 * Lo conocido con un toque, y «Otra» para lo que no está.
 *
 * La marca de un perfume se escribía a mano, y «LATTAFA», «Lattafa» y
 * «LATAFFA» terminaban siendo tres marcas. Las que ya se venden quedan a un
 * toque; una nueva se escribe una vez, y ahí queda.
 */
function ChipsConOtra({
  campo,
  valor,
  onValor,
  error,
}: {
  campo: CampoAlta;
  valor: string | number | null;
  onValor: (v: string | number | null) => void;
  error?: string;
}): JSX.Element {
  const { label, ayuda, placeholder, requerido = false } = campo;
  const opciones = (campo.opciones ?? []).map(String);
  const numerico = typeof campo.opciones?.[0] === "number";
  const texto = valor === null ? "" : String(valor);
  const conocido = opciones.includes(texto.toUpperCase());
  // «Otra» queda abierta si se tocó, o si lo cargado no está entre los botones.
  const [otra, setOtra] = useState(texto !== "" && !conocido);
  const OTRA = "__otra__";
  const etiquetaOpcion = (o: string): string => (numerico ? `${o} ml` : o);

  return (
    <div className="space-y-3">
      <GrupoCampo
        label={label}
        ayuda={ayuda}
        error={otra ? undefined : error}
        requerido={requerido}
      >
        <Segmented
          apariencia="chips"
          tamano="md"
          etiqueta={label}
          opciones={[
            ...opciones.map((o) => ({ valor: o, etiqueta: etiquetaOpcion(o) })),
            { valor: OTRA, etiqueta: numerico ? "Otro" : "Otra" },
          ]}
          valor={otra ? OTRA : conocido ? texto.toUpperCase() : ""}
          onChange={(v) => {
            if (v === OTRA) {
              setOtra(true);
              onValor(numerico ? null : "");
              return;
            }
            setOtra(false);
            onValor(numerico ? Number(v) : v);
          }}
        />
      </GrupoCampo>
      {otra ? (
        numerico ? (
          <CampoCantidad
            label={`${label}, otro`}
            error={error}
            requerido={requerido}
            placeholder={placeholder}
            valor={typeof valor === "number" ? valor : null}
            onValorChange={(v) => onValor(v)}
          />
        ) : (
          <Campo label={`${label}, otra`} error={error} requerido={requerido}>
            <Input
              autoComplete="off"
              placeholder={placeholder}
              value={texto}
              onChange={(e) => onValor(e.target.value)}
            />
          </Campo>
        )
      ) : null}
    </div>
  );
}
